// Modern Mobile Interactions & Animations

class ModernMobileUI {
    constructor() {
        this.init();
    }

    init() {
        this.setupEventListeners();
        this.initializeAnimations();
        // this.setupTouchInteractions();
        this.initializeToastSystem();
        this.setupSmoothScrolling();
        this.initializeLazyLoading();
        this.setupFormEnhancements();
        this.initializeProgressIndicators();
    }

    setupEventListeners() {
        // Modern button interactions
        document.addEventListener('click', (e) => {
            const el = e.target instanceof Element ? e.target : e.target.parentElement;
            if (el && (el.classList.contains('btn-modern') || el.closest('.btn-modern'))) {
                this.createRippleEffect(e);
            }
        });

        // Form interactions
        document.addEventListener('focusin', (e) => {
            if (e.target.matches('.input-modern input, .input-modern select')) {
                this.enhanceFormField(e.target);
            }
        });

        // Card interactions
        document.addEventListener('mouseenter', (e) => {
            let card = null;
            // Polyfill for closest: traverse up manually if not available
            let el = e.target;
            while (el) {
                if (el.classList && el.classList.contains('card-modern')) {
                    card = el;
                    break;
                }
                el = el.parentElement;
            }
            if (card) {
                this.animateCard(card);
            }
        });

        // Touch gestures
        this.setupTouchGestures();
    }

    createRippleEffect(event) {
        const button = event.target.classList.contains('btn-modern') ? 
            event.target : event.target.closest('.btn-modern');
        
        if (!button) return;

        const ripple = document.createElement('span');
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = event.clientX - rect.left - size / 2;
        const y = event.clientY - rect.top - size / 2;

        ripple.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            left: ${x}px;
            top: ${y}px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
        `;

        button.style.position = 'relative';
        button.appendChild(ripple);

        setTimeout(() => {
            ripple.remove();
        }, 600);
    }

    enhanceFormField(field) {
        const container = field.closest('.input-modern');
        if (!container) return;

        container.classList.add('focused');
        
        // Add floating label animation
        const label = container.querySelector('label');
        if (label) {
            label.style.transform = 'translateY(-50%) scale(0.8)';
            label.style.color = 'var(--primary-color)';
        }
    }

    animateCard(card) {
        card.style.transform = 'translateY(-8px) scale(1.02)';
        card.style.boxShadow = 'var(--shadow-heavy)';
        
        setTimeout(() => {
            card.style.transform = 'translateY(-4px) scale(1.01)';
        }, 150);
    }

    setupTouchGestures() {
        let startX, startY, startTime;
        let isScrolling = false;

        document.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            startTime = Date.now();
            isScrolling = false;
        });

        document.addEventListener('touchmove', (e) => {
            if (!startX || !startY) return;

            const deltaX = Math.abs(e.touches[0].clientX - startX);
            const deltaY = Math.abs(e.touches[0].clientY - startY);

            if (deltaY > deltaX && deltaY > 10) {
                isScrolling = true;
            }
        });

        document.addEventListener('touchend', (e) => {
            if (!startX || !startY || isScrolling) return;

            const deltaTime = Date.now() - startTime;
            const deltaX = e.changedTouches[0].clientX - startX;
            const deltaY = e.changedTouches[0].clientY - startY;

            if (deltaTime < 300 && Math.abs(deltaX) < 50 && Math.abs(deltaY) < 50) {
                this.handleTap(e);
            }

            startX = startY = null;
        });
    }

    handleTap(event) {
        const target = event.target.closest('.interactive-element, .card-modern, .nav-item');
        if (target) {
            this.createTapFeedback(target, event);
        }
    }

    createTapFeedback(element, event) {
        const feedback = document.createElement('div');
        const rect = element.getBoundingClientRect();
        const size = Math.min(rect.width, rect.height) * 0.8;
        const x = event.changedTouches[0].clientX - rect.left - size / 2;
        const y = event.changedTouches[0].clientY - rect.top - size / 2;

        feedback.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            left: ${x}px;
            top: ${y}px;
            background: rgba(22, 128, 240, 0.2);
            border-radius: 50%;
            transform: scale(0);
            animation: tapFeedback 0.4s ease-out;
            pointer-events: none;
            z-index: 1000;
        `;

        element.style.position = 'relative';
        element.appendChild(feedback);

        setTimeout(() => {
            feedback.remove();
        }, 400);
    }

    initializeAnimations() {
        // Intersection Observer for scroll animations
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in-up');
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        // Observe elements for animation
        document.querySelectorAll('.card-modern, .form-modern, .nav-modern').forEach(el => {
            observer.observe(el);
        });
    }

    setupSmoothScrolling() {
        // Smooth scroll for anchor links
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', (e) => {
                e.preventDefault();
                const target = document.querySelector(anchor.getAttribute('href'));
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });
    }

    initializeLazyLoading() {
        // Lazy load images
        const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    img.src = img.dataset.src;
                    img.classList.remove('loading-skeleton');
                    imageObserver.unobserve(img);
                }
            });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
            img.classList.add('loading-skeleton');
            imageObserver.observe(img);
        });
    }

    setupFormEnhancements() {
        // Auto-resize textareas
        document.querySelectorAll('textarea').forEach(textarea => {
            textarea.addEventListener('input', () => {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
            });
        });

        // Form validation with modern feedback
        document.querySelectorAll('form').forEach(form => {
            form.addEventListener('submit', (e) => {
                if (!this.validateForm(form)) {
                    e.preventDefault();
                    this.showToast('Please check your input and try again.', 'error');
                }
            });
        });
    }

    validateForm(form) {
        let isValid = true;
        const inputs = form.querySelectorAll('input[required], select[required], textarea[required]');

        inputs.forEach(input => {
            if (!input.value.trim()) {
                this.highlightError(input);
                isValid = false;
            } else {
                this.removeError(input);
            }
        });

        return isValid;
    }

    highlightError(input) {
        input.style.borderColor = 'var(--error-color)';
        input.style.boxShadow = '0 0 0 3px rgba(244, 67, 54, 0.1)';
    }

    removeError(input) {
        input.style.borderColor = 'var(--border-light)';
        input.style.boxShadow = 'none';
    }

    initializeToastSystem() {
        this.toastContainer = document.createElement('div');
        this.toastContainer.className = 'toast-container';
        this.toastContainer.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 10000;
            display: flex;
            flex-direction: column;
            gap: 10px;
        `;
        document.body.appendChild(this.toastContainer);
    }

    showToast(message, type = 'info', duration = 3000) {
        const toast = document.createElement('div');
        toast.className = `toast-modern ${type}`;
        toast.innerHTML = `
            <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 18px;">${this.getToastIcon(type)}</span>
                <span>${message}</span>
                <button onclick="this.parentElement.parentElement.remove()" 
                        style="background: none; border: none; color: inherit; cursor: pointer; margin-left: auto;">
                    ×
                </button>
            </div>
        `;

        this.toastContainer.appendChild(toast);

        // Trigger animation
        setTimeout(() => {
            toast.classList.add('show');
        }, 100);

        // Auto remove
        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => {
                if (toast.parentElement) {
                    toast.remove();
                }
            }, 300);
        }, duration);
    }

    getToastIcon(type) {
        const icons = {
            success: '✅',
            error: '❌',
            warning: '⚠️',
            info: 'ℹ️'
        };
        return icons[type] || icons.info;
    }

    initializeProgressIndicators() {
        // Create progress bar for page load
        const progressBar = document.createElement('div');
        progressBar.className = 'progress-modern page-progress';
        progressBar.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 3px;
            z-index: 10001;
            background: transparent;
        `;
        progressBar.innerHTML = '<div class="progress-bar" style="width: 0%;"></div>';
        document.body.appendChild(progressBar);

        // Simulate progress
        let progress = 0;
        const interval = setInterval(() => {
            progress += Math.random() * 30;
            if (progress > 90) {
                progress = 90;
            }
            progressBar.querySelector('.progress-bar').style.width = progress + '%';
        }, 200);

        window.addEventListener('load', () => {
            clearInterval(interval);
            progressBar.querySelector('.progress-bar').style.width = '100%';
            setTimeout(() => {
                progressBar.style.opacity = '0';
                setTimeout(() => {
                    progressBar.remove();
                }, 300);
            }, 500);
        });
    }

    // Utility methods
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
    @keyframes ripple {
        to {
            transform: scale(4);
            opacity: 0;
        }
    }

    @keyframes tapFeedback {
        0% {
            transform: scale(0);
            opacity: 1;
        }
        100% {
            transform: scale(1);
            opacity: 0;
        }
    }

    .input-modern.focused input,
    .input-modern.focused select {
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px rgba(22, 128, 240, 0.1);
    }

    .card-modern {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .btn-modern {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .fab {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .toast-modern {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.modernUI = new ModernMobileUI();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ModernMobileUI;
} 